import { Component, input, output, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
	SetupWizardContentFormPayload,
	CONTENT_CSV_LABELS,
	ContentCsvKeys,
	ContentCsvTemplate,
	CONTENT_CSV_SPEC,
} from '@mira-avant/mtlibs';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { TextField } from '../../../fields/setup-wizard/text-field/text-field';
import { CheckboxField } from '../../../fields/setup-wizard/checkbox-field/checkbox-field';
import { IntegerField } from '../../../fields/setup-wizard/integer-field/integer-field';
import { LanguageDropdownField } from '../../../fields/setup-wizard/language-dropdown-field/language-dropdown-field';
import { TextareaField } from '../../../fields/setup-wizard/textarea-field/textarea-field';
import { FormUtil } from '../../../../utils/form.util';
import { ContentTypeDropdownField } from '../../../fields/setup-wizard/content-type-dropdown-field/content-type-dropdown-field';
import { ScoringMethodDropdownField } from '../../../fields/setup-wizard/scoring-method-dropdown-field/scoring-method-dropdown-field';
import { SkillCodeDropdownField } from '../../../fields/setup-wizard/skill-code-dropdown-field/skill-code-dropdown-field';
import { RubricLevelDropdownField } from '../../../fields/setup-wizard/rubric-level-dropdown-field/rubric-level-dropdown-field';
import { ContentService } from '../../../../services/content.service';

type ContentForm = {
	[ContentCsvKeys.ssoContentId]: FormControl<string | null>;
	[ContentCsvKeys.ssoActivityId]: FormControl<string | null>;
	[ContentCsvKeys.defaultLangId]: FormControl<string | null>;
	[ContentCsvKeys.displayOrder]: FormControl<number | null>;
	[ContentCsvKeys.isInstruction]: FormControl<boolean | null>;
	[ContentCsvKeys.contentType]: FormControl<string | null>;
	[ContentCsvKeys.featuredMode]: FormControl<boolean | null>;
	[ContentCsvKeys.scoringMethod]: FormControl<string | null>;
	[ContentCsvKeys.skillCode]: FormControl<string | null>;
	[ContentCsvKeys.rubricLevel]: FormControl<number | null>;
	[ContentCsvKeys.enName]: FormControl<string | null>;
	[ContentCsvKeys.enImageUri]: FormControl<string | null>;
	[ContentCsvKeys.enContentUri]: FormControl<string | null>;
	[ContentCsvKeys.enContentTags]: FormControl<string | null>;
	[ContentCsvKeys.enDescriptionShort]: FormControl<string | null>;
	[ContentCsvKeys.enDescriptionHtml]: FormControl<string | null>;
	[ContentCsvKeys.esName]: FormControl<string | null>;
	[ContentCsvKeys.esImageUri]: FormControl<string | null>;
	[ContentCsvKeys.esContentUri]: FormControl<string | null>;
	[ContentCsvKeys.esContentTags]: FormControl<string | null>;
	[ContentCsvKeys.esDescriptionShort]: FormControl<string | null>;
	[ContentCsvKeys.esDescriptionHtml]: FormControl<string | null>;
	[ContentCsvKeys.contentUuid]: FormControl<string | null>;
	[ContentCsvKeys.activityUuid]: FormControl<string | null>;
	[ContentCsvKeys.active]: FormControl<boolean | null>;
};

@Component({
	selector: 'app-setup-wizard-content-form',
	imports: [
		ReactiveFormsModule,
		NzButtonModule,
		NzFormModule,
		NzInputModule,
		NzSpinModule,
		TextField,
		CheckboxField,
		IntegerField,
		LanguageDropdownField,
		TextareaField,
		ContentTypeDropdownField,
		ScoringMethodDropdownField,
		SkillCodeDropdownField,
		RubricLevelDropdownField,
	],
	standalone: true,
	templateUrl: './setup-wizard-content-form.html',
	styleUrl: './setup-wizard-content-form.scss',
})
export class SetupWizardContentForm {
	public activityUuid = input.required<string>();
	public edit = input<ContentCsvTemplate | null>(null);
	public loading = input<boolean>(false);
	public onClear = output<void>();
	public onSubmit = output<SetupWizardContentFormPayload>();
	public rubricCode = signal<string | undefined>(undefined);
	public ContentCsvKeys = ContentCsvKeys;
	public CONTENT_CSV_LABELS = CONTENT_CSV_LABELS;
	public Validators = Validators;

	public form = new FormGroup<ContentForm>({
		[ContentCsvKeys.ssoContentId]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[ContentCsvKeys.ssoActivityId]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[ContentCsvKeys.defaultLangId]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[ContentCsvKeys.displayOrder]: new FormControl<number | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[ContentCsvKeys.isInstruction]: new FormControl<boolean | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[ContentCsvKeys.contentType]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[ContentCsvKeys.featuredMode]: new FormControl<boolean | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[ContentCsvKeys.scoringMethod]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[ContentCsvKeys.skillCode]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[ContentCsvKeys.rubricLevel]: new FormControl<number | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[ContentCsvKeys.enName]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[ContentCsvKeys.enImageUri]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[ContentCsvKeys.enContentUri]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[ContentCsvKeys.enContentTags]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[ContentCsvKeys.enDescriptionShort]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[ContentCsvKeys.enDescriptionHtml]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[ContentCsvKeys.esName]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[ContentCsvKeys.esImageUri]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[ContentCsvKeys.esContentUri]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[ContentCsvKeys.esContentTags]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[ContentCsvKeys.esDescriptionShort]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[ContentCsvKeys.esDescriptionHtml]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[ContentCsvKeys.contentUuid]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[ContentCsvKeys.activityUuid]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[ContentCsvKeys.active]: new FormControl<boolean | null>(null, {
			nonNullable: false,
			validators: [],
		}),
	});

	constructor(private contentService: ContentService) {
		FormUtil.applyCsvSpecValidators(this.form, CONTENT_CSV_SPEC);
	}

	async ngOnChanges() {
		const edit = this.edit();

		if (edit) {
			if (edit.content_uuid) {
				const rubricCode: string | undefined = await this.contentService.getRubricCode$(edit.content_uuid);
				this.rubricCode.set(rubricCode);
			}

			// If edit contains a contentUuid we can remove all validators for ssoContentId
			if (edit[ContentCsvKeys.contentUuid]) {
				this.form.get(ContentCsvKeys.ssoContentId)?.clearValidators();
				this.form.get(ContentCsvKeys.ssoContentId)?.updateValueAndValidity();
			}

			// If edit contains a activityUuid we can remove all validators for ssoActivityId
			if (edit[ContentCsvKeys.activityUuid]) {
				this.form.get(ContentCsvKeys.ssoActivityId)?.clearValidators();
				this.form.get(ContentCsvKeys.ssoActivityId)?.updateValueAndValidity();
			}

			this.form.patchValue({
				[ContentCsvKeys.ssoContentId]: edit[ContentCsvKeys.ssoContentId],
				[ContentCsvKeys.ssoActivityId]: edit[ContentCsvKeys.ssoActivityId],
				[ContentCsvKeys.defaultLangId]: edit[ContentCsvKeys.defaultLangId],
				[ContentCsvKeys.displayOrder]: edit[ContentCsvKeys.displayOrder],
				[ContentCsvKeys.isInstruction]: edit[ContentCsvKeys.isInstruction],
				[ContentCsvKeys.contentType]: edit[ContentCsvKeys.contentType],
				[ContentCsvKeys.featuredMode]: edit[ContentCsvKeys.featuredMode],
				[ContentCsvKeys.scoringMethod]: edit[ContentCsvKeys.scoringMethod],
				[ContentCsvKeys.skillCode]: edit[ContentCsvKeys.skillCode],
				[ContentCsvKeys.rubricLevel]: Number(edit[ContentCsvKeys.rubricLevel]) ?? undefined,
				[ContentCsvKeys.enName]: edit[ContentCsvKeys.enName],
				[ContentCsvKeys.enImageUri]: edit[ContentCsvKeys.enImageUri],
				[ContentCsvKeys.enContentUri]: edit[ContentCsvKeys.enContentUri],
				[ContentCsvKeys.enContentTags]: edit[ContentCsvKeys.enContentTags],
				[ContentCsvKeys.enDescriptionShort]: edit[ContentCsvKeys.enDescriptionShort],
				[ContentCsvKeys.enDescriptionHtml]: edit[ContentCsvKeys.enDescriptionHtml],
				[ContentCsvKeys.esName]: edit[ContentCsvKeys.esName],
				[ContentCsvKeys.esImageUri]: edit[ContentCsvKeys.esImageUri],
				[ContentCsvKeys.esContentUri]: edit[ContentCsvKeys.esContentUri],
				[ContentCsvKeys.esContentTags]: edit[ContentCsvKeys.esContentTags],
				[ContentCsvKeys.esDescriptionShort]: edit[ContentCsvKeys.esDescriptionShort],
				[ContentCsvKeys.esDescriptionHtml]: edit[ContentCsvKeys.esDescriptionHtml],
				[ContentCsvKeys.contentUuid]: edit[ContentCsvKeys.contentUuid],
				[ContentCsvKeys.activityUuid]: edit[ContentCsvKeys.activityUuid],
				[ContentCsvKeys.active]: edit[ContentCsvKeys.active],
			});
		} else {
			const activityUuid = this.activityUuid();

			if (!activityUuid) {
				throw new Error('Activity UUID is required');
			}

			this.form.patchValue({
				[ContentCsvKeys.activityUuid]: this.activityUuid(),
			});

			this.rubricCode.set(undefined);
		}
	}

	onFormSubmit() {
		if (this.form.valid) {
			const value = this.form.getRawValue();

			this.onSubmit.emit({
				[ContentCsvKeys.ssoContentId]: value[ContentCsvKeys.ssoContentId] ?? undefined,
				[ContentCsvKeys.ssoActivityId]: value[ContentCsvKeys.ssoActivityId] ?? undefined,
				[ContentCsvKeys.defaultLangId]: value[ContentCsvKeys.defaultLangId] ?? undefined,
				[ContentCsvKeys.displayOrder]: value[ContentCsvKeys.displayOrder] ?? undefined,
				[ContentCsvKeys.isInstruction]: value[ContentCsvKeys.isInstruction] ?? undefined,
				[ContentCsvKeys.contentType]: value[ContentCsvKeys.contentType] ?? undefined,
				[ContentCsvKeys.featuredMode]: value[ContentCsvKeys.featuredMode] ?? undefined,
				[ContentCsvKeys.scoringMethod]: value[ContentCsvKeys.scoringMethod] ?? undefined,
				[ContentCsvKeys.skillCode]: value[ContentCsvKeys.skillCode] ?? undefined,
				[ContentCsvKeys.rubricLevel]: value[ContentCsvKeys.rubricLevel] ?? undefined,
				[ContentCsvKeys.enName]: value[ContentCsvKeys.enName] ?? undefined,
				[ContentCsvKeys.enImageUri]: value[ContentCsvKeys.enImageUri] ?? undefined,
				[ContentCsvKeys.enContentUri]: value[ContentCsvKeys.enContentUri] ?? undefined,
				[ContentCsvKeys.enContentTags]: value[ContentCsvKeys.enContentTags] ?? undefined,
				[ContentCsvKeys.enDescriptionShort]: value[ContentCsvKeys.enDescriptionShort] ?? undefined,
				[ContentCsvKeys.enDescriptionHtml]: value[ContentCsvKeys.enDescriptionHtml] ?? undefined,
				[ContentCsvKeys.esName]: value[ContentCsvKeys.esName] ?? undefined,
				[ContentCsvKeys.esImageUri]: value[ContentCsvKeys.esImageUri] ?? undefined,
				[ContentCsvKeys.esContentUri]: value[ContentCsvKeys.esContentUri] ?? undefined,
				[ContentCsvKeys.esContentTags]: value[ContentCsvKeys.esContentTags] ?? undefined,
				[ContentCsvKeys.esDescriptionShort]: value[ContentCsvKeys.esDescriptionShort] ?? undefined,
				[ContentCsvKeys.esDescriptionHtml]: value[ContentCsvKeys.esDescriptionHtml] ?? undefined,
				[ContentCsvKeys.contentUuid]: value[ContentCsvKeys.contentUuid] ?? undefined,
				[ContentCsvKeys.activityUuid]: value[ContentCsvKeys.activityUuid] ?? undefined,
				[ContentCsvKeys.active]: value[ContentCsvKeys.active] ?? undefined,
			});

			this.form.reset();
			this.rubricCode.set(undefined);
		} else {
			this.form.markAllAsTouched();
		}
	}

	onClearClicked() {
		this.onClear.emit();
	}
}
